/**
 * Form Templates for Form Builder V2
 *
 * Provides pre-built templates for common form use cases:
 * - Review: Customer satisfaction with Google redirect
 * - Quick Feedback: Simple emoji/thumbs rating
 * - Product Finder: Guided discovery to match products/services
 * - Onboarding: Customer/employee data collection
 */

import {
  Star,
  MessageCircle,
  Sparkles,
  UserPlus,
  FileEdit,
  LucideIcon,
} from 'lucide-react';
import { QuestionConfig, QuestionType } from './question-types';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type FormTemplateType = 'review' | 'quick_feedback' | 'quiz' | 'onboarding' | 'custom';

export interface QuizConfig {
  showProgressBar: boolean;          // Show progress through the quiz
  collectEmailAtEnd: boolean;        // Ask for email before showing results
  emailRequired: boolean;            // Is email required to see results
  showResultsPage: boolean;          // Show a results/recommendation page
  resultsTitle?: string;             // Custom title for results page
  resultsDescription?: string;       // Custom description for results
}

export interface NotificationRecipient {
  type: 'email' | 'whatsapp';
  value: string;                     // Email address or phone number
  name?: string;                     // Optional display name
}

export interface NotificationConfig {
  emailEnabled: boolean;
  emailRecipients: string[];         // List of email addresses
  whatsappEnabled: boolean;
  whatsappNumbers: string[];         // List of phone numbers
  dashboardOnly: boolean;            // Only show in dashboard, no notifications
  notifyOnSubmission: boolean;       // Send notification when form is submitted
  emailSubject?: string;             // Custom email subject
  emailTemplate?: string;            // Custom email template ID
}

export interface TemplateQuestion extends Omit<QuestionConfig, 'deviceConfig'> {
  // Optional helper text shown below the question
  helperText?: string;
}

export interface FormTemplate {
  id: FormTemplateType;
  name: string;
  description: string;
  icon: LucideIcon;
  color: string;                     // Tailwind color class
  defaultQuestions: TemplateQuestion[];
  defaultConfig: {
    enableStep2?: boolean;
    enableStep2High?: boolean;
    enableStep2Low?: boolean;
    quizConfig?: QuizConfig;
    notificationConfig?: NotificationConfig;
  };
  features: string[];                // Feature highlights
  category: 'feedback' | 'assessment' | 'collection';
}

// ============================================
// DEFAULT CONFIGURATIONS
// ============================================

export const defaultQuizConfig: QuizConfig = {
  showProgressBar: true,
  collectEmailAtEnd: true,
  emailRequired: false,
  showResultsPage: true,
  resultsTitle: 'Your Recommendation',
  resultsDescription: 'Based on your answers, here is what we recommend for you.',
};

export const defaultNotificationConfig: NotificationConfig = {
  emailEnabled: false,
  emailRecipients: [],
  whatsappEnabled: false,
  whatsappNumbers: [],
  dashboardOnly: true,
  notifyOnSubmission: true,
};

// ============================================
// REVIEW SETTINGS (for rating-based forms)
// ============================================

export type RewardType = 'none' | 'coupon' | 'loyalty';

export interface ReviewSettings {
  // Rating threshold (1-5, default 4)
  // Ratings at or above this threshold go to Google Reviews
  starThreshold: number;

  // Google Review URL
  googlePlaceId: string | null;
  googleReviewUrl: string;
  googleReviewUrlOverride?: string;

  // High Rating Path (ratings >= threshold)
  highRatingEmailEnabled: boolean;
  highRatingEmailRequired: boolean;
  highRatingRewardType: RewardType;
  highRatingCouponOfferId: string | null;
  highRatingLoyaltyProgramId: string | null;

  // Low Rating Path (ratings < threshold)
  lowRatingEmailEnabled: boolean;
  lowRatingEmailRequired: boolean;
  lowRatingRewardType: RewardType;
  lowRatingCouponOfferId: string | null;
  lowRatingLoyaltyProgramId: string | null;
}

export const defaultReviewSettings: ReviewSettings = {
  starThreshold: 4,
  googlePlaceId: null,
  googleReviewUrl: '',
  highRatingEmailEnabled: false,
  highRatingEmailRequired: false,
  highRatingRewardType: 'none',
  highRatingCouponOfferId: null,
  highRatingLoyaltyProgramId: null,
  lowRatingEmailEnabled: false,
  lowRatingEmailRequired: false,
  lowRatingRewardType: 'none',
  lowRatingCouponOfferId: null,
  lowRatingLoyaltyProgramId: null,
};

// ============================================
// TEMPLATE DEFINITIONS
// ============================================

export const formTemplates: FormTemplate[] = [
  // Review Template
  {
    id: 'review',
    name: 'Customer Review',
    description: 'Collect customer feedback with star rating and Google redirect for happy customers',
    icon: Star,
    color: 'yellow',
    category: 'feedback',
    features: [
      'Star rating',
      'Conditional follow-up',
      'Google redirect for high ratings',
      'Detailed feedback for improvements',
    ],
    defaultConfig: {
      enableStep2: true,
      enableStep2High: true,
      enableStep2Low: true,
    },
    defaultQuestions: [
      {
        type: 'short_text',
        text: 'What did you enjoy most about your experience?',
        required: false,
        placeholder: 'Share what made your visit special...',
        visibleOnHighRating: true,
        visibleOnLowRating: false,
      },
      {
        type: 'long_text',
        text: 'How can we improve your experience?',
        required: false,
        placeholder: 'Your feedback helps us get better...',
        visibleOnHighRating: false,
        visibleOnLowRating: true,
      },
      {
        type: 'email',
        text: 'Would you like us to follow up? Leave your email (optional)',
        required: false,
        placeholder: 'your@email.com',
        visibleOnHighRating: true,
        visibleOnLowRating: true,
      },
    ],
  },

  // Quick Feedback Template
  {
    id: 'quick_feedback',
    name: 'Quick Feedback',
    description: 'Simple one-tap feedback with emoji or thumbs up/down',
    icon: MessageCircle,
    color: 'blue',
    category: 'feedback',
    features: [
      'One-tap rating',
      'Minimal friction',
      'Optional follow-up',
      'Fast completion',
    ],
    defaultConfig: {
      enableStep2: true,
      enableStep2High: false,
      enableStep2Low: true,
    },
    defaultQuestions: [
      {
        type: 'long_text',
        text: 'Anything you would like to share with us?',
        required: false,
        placeholder: 'Your thoughts...',
        visibleOnHighRating: true,
        visibleOnLowRating: true,
      },
    ],
  },

  // Product Finder / Recommendation Quiz Template
  {
    id: 'quiz',
    name: 'Product Finder',
    description: 'Guide customers to the right product or service with a series of questions',
    icon: Sparkles,
    color: 'purple',
    category: 'collection',
    features: [
      'Guided discovery',
      'Product matching',
      'Lead capture',
      'Personalized results',
    ],
    defaultConfig: {
      enableStep2: false,
      enableStep2High: false,
      enableStep2Low: false,
      quizConfig: defaultQuizConfig,
    },
    defaultQuestions: [
      {
        type: 'dropdown',
        text: 'What is your primary goal?',
        required: true,
        options: [
          { id: '1', label: 'Save time', value: 'save_time' },
          { id: '2', label: 'Save money', value: 'save_money' },
          { id: '3', label: 'Improve quality', value: 'improve_quality' },
          { id: '4', label: 'Try something new', value: 'try_new' },
        ],
      },
      {
        type: 'dropdown',
        text: 'What is your budget?',
        required: true,
        options: [
          { id: '1', label: 'Under $50', value: 'budget_low' },
          { id: '2', label: '$50 - $100', value: 'budget_mid' },
          { id: '3', label: '$100 - $250', value: 'budget_high' },
          { id: '4', label: '$250+', value: 'budget_premium' },
        ],
      },
      {
        type: 'checkboxes',
        text: 'Which features are most important to you?',
        required: true,
        options: [
          { id: '1', label: 'Easy to use', value: 'easy_to_use' },
          { id: '2', label: 'Fast results', value: 'fast_results' },
          { id: '3', label: 'Great support', value: 'great_support' },
          { id: '4', label: 'Best value', value: 'best_value' },
        ],
      },
      {
        type: 'dropdown',
        text: 'How soon do you need a solution?',
        required: true,
        options: [
          { id: '1', label: 'Immediately', value: 'asap' },
          { id: '2', label: 'Within a week', value: 'week' },
          { id: '3', label: 'Within a month', value: 'month' },
          { id: '4', label: 'Just exploring', value: 'exploring' },
        ],
      },
    ],
  },

  // Onboarding Template
  {
    id: 'onboarding',
    name: 'Onboarding / Registration',
    description: 'Collect customer or employee details with notifications to your team',
    icon: UserPlus,
    color: 'green',
    category: 'collection',
    features: [
      'Contact details collection',
      'Email notifications',
      'WhatsApp alerts',
      'Dashboard tracking',
    ],
    defaultConfig: {
      enableStep2: false,
      enableStep2High: false,
      enableStep2Low: false,
      notificationConfig: {
        ...defaultNotificationConfig,
        emailEnabled: true,
        notifyOnSubmission: true,
      },
    },
    defaultQuestions: [
      {
        type: 'short_text',
        text: 'What is your full name?',
        required: true,
        placeholder: 'John Smith',
      },
      {
        type: 'email',
        text: 'What is your email address?',
        required: true,
        placeholder: 'john@example.com',
      },
      {
        type: 'phone',
        text: 'What is your phone number?',
        required: false,
        placeholder: '+1 (555) 000-0000',
      },
      {
        type: 'dropdown',
        text: 'How did you hear about us?',
        required: false,
        options: [
          { id: '1', label: 'Google Search', value: 'google' },
          { id: '2', label: 'Social Media', value: 'social_media' },
          { id: '3', label: 'Friend Referral', value: 'referral' },
          { id: '4', label: 'Advertisement', value: 'advertisement' },
          { id: '5', label: 'Other', value: 'other' },
        ],
      },
      {
        type: 'date',
        text: 'Preferred appointment date',
        required: false,
      },
      {
        type: 'long_text',
        text: 'Any additional notes or requests?',
        required: false,
        placeholder: 'Tell us more about what you need...',
      },
    ],
  },

  // Custom/Blank Template
  {
    id: 'custom',
    name: 'Start from Scratch',
    description: 'Create a custom form with any combination of question types',
    icon: FileEdit,
    color: 'gray',
    category: 'collection',
    features: [
      'Full customization',
      'All question types',
      'Custom logic',
      'Flexible design',
    ],
    defaultConfig: {
      enableStep2: false,
      enableStep2High: false,
      enableStep2Low: false,
    },
    defaultQuestions: [],
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getTemplateById(id: FormTemplateType): FormTemplate | undefined {
  return formTemplates.find(t => t.id === id);
}

export function getTemplatesByCategory(category: FormTemplate['category']): FormTemplate[] {
  return formTemplates.filter(t => t.category === category);
}

export function isQuizTemplate(templateType: FormTemplateType): boolean {
  return templateType === 'quiz';
}

export function isOnboardingTemplate(templateType: FormTemplateType): boolean {
  return templateType === 'onboarding';
}

export function needsRatingStep(templateType: FormTemplateType): boolean {
  return templateType === 'review' || templateType === 'quick_feedback';
}

export function getTemplateColor(templateType: FormTemplateType): string {
  const template = getTemplateById(templateType);
  const colorMap: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  };
  return colorMap[template?.color || 'gray'] || colorMap.gray;
}

export function getTemplateBorderColor(templateType: FormTemplateType): string {
  const template = getTemplateById(templateType);
  const colorMap: Record<string, string> = {
    yellow: 'border-yellow-300 dark:border-yellow-700',
    blue: 'border-blue-300 dark:border-blue-700',
    purple: 'border-purple-300 dark:border-purple-700',
    green: 'border-green-300 dark:border-green-700',
    gray: 'border-gray-300 dark:border-gray-700',
  };
  return colorMap[template?.color || 'gray'] || colorMap.gray;
}
